import type { Posting } from "@/lib/api/catalogue";
import { checkPageName, NO_SUCH_POSTING, postingPageName } from "@/lib/posting/page";
import { postingDetailPath, resumeCheckPath } from "@/lib/routes";
import { postingJsonLd, seenLately } from "./jobPosting";
import type { SitePage } from "./pages";

/**
 * A posting's head, as a `SitePage` for the server to write with
 * `headTags` (./pages.ts) into the posting shell. Pure.
 */

/** The built copy of the app's page that the server fills in for a posting. */
export const POSTING_SHELL_FILE = "posting-shell.html";

/** What a search result shows: the description at most this long. */
const DESCRIPTION_LIMIT = 155;

/** The fewest keywords that earn a check page a place in the index: fewer is a thin page. */
export const MIN_CHECK_KEYWORDS = 3;

/** The text cut to `limit` characters at a word, with an ellipsis, or whole if it fits. */
function shortened(text: string, limit: number): string {
    if (text.length <= limit) return text;
    let cut = text.slice(0, limit - 1);
    // Never half of an emoji.
    if (/[\ud800-\udbff]$/.test(cut)) cut = cut.slice(0, -1);
    const lastSpace = cut.lastIndexOf(" ");
    if (lastSpace > 0) cut = cut.slice(0, lastSpace);
    return `${cut.trimEnd()}…`;
}

/**
 * The head of one posting at `now`. An open posting its board still lists is indexed, at its
 * own address, with its `JobPosting` data. A closed one, or one its board has stopped listing,
 * still renders, for the people whose history it is in, but is kept out of the index and has
 * no data. A posting has no markdown copy.
 */
export function postingPage(posting: Posting, now: Date): SitePage {
    const words = posting.description.replaceAll(/\s+/g, " ").trim();
    const lead = `${postingPageName(posting)}.`;
    return {
        path: postingDetailPath(posting.posting_id),
        file: POSTING_SHELL_FILE,
        title: `${postingPageName(posting)} · Job Envoy`,
        description: shortened(words === "" ? lead : `${lead} ${words}`, DESCRIPTION_LIMIT),
        indexed: posting.closed_at === null && seenLately(posting, now),
        markdown: false,
        structuredData: (siteUrl) => postingJsonLd(posting, siteUrl, now),
    };
}

/** The keywords a posting names. A row can lack them, and that is none. */
function keywordCount(posting: Posting): number {
    return posting.keywords?.length ?? 0;
}

/** What a check page's description promises. A preview shows it, in the index or out. */
function checkDescription(count: number): string {
    const offer = "Free, no sign-up. Then have AI tailor it.";
    if (count === 0) return `See what your resume says for this job. ${offer}`;
    if (count === 1) return `See whether the 1 skill this job names is on your resume. ${offer}`;
    return `See which of the ${count} skills this job names are on your resume. ${offer}`;
}

/**
 * Whether a posting's check page is worth an index's time at `now`: the job is open, its board
 * still lists it, and it names enough keywords that the page says something of its own.
 */
export function isCheckable(posting: Posting, now: Date): boolean {
    return (
        posting.closed_at === null &&
        seenLately(posting, now) &&
        keywordCount(posting) >= MIN_CHECK_KEYWORDS
    );
}

/**
 * The head of a posting's check page at `now`. Rendered for any posting, in the index only when
 * it `isCheckable`. It has no `JobPosting` data, so the posting's own page stays the one Google
 * lists as a job.
 */
export function checkPage(posting: Posting, now: Date): SitePage {
    return {
        path: resumeCheckPath(posting.posting_id),
        file: POSTING_SHELL_FILE,
        title: `${checkPageName(posting)} · Job Envoy`,
        description: checkDescription(keywordCount(posting)),
        indexed: isCheckable(posting, now),
        markdown: false,
    };
}

/** The head for an address that names no posting: the page says so, and stays out of the index. */
export function missingPostingPage(): SitePage {
    return {
        path: postingDetailPath(""),
        file: POSTING_SHELL_FILE,
        title: "Posting not found · Job Envoy",
        description: NO_SUCH_POSTING,
        indexed: false,
        markdown: false,
    };
}

/**
 * The head while a posting cannot be read: the catalogue is busy or down, and the posting
 * may well be there. It stays out of the index and says nothing of the posting either way.
 */
export function unavailablePostingPage(): SitePage {
    return {
        path: postingDetailPath(""),
        file: POSTING_SHELL_FILE,
        title: "Job Envoy",
        description: "This job will be back shortly.",
        indexed: false,
        markdown: false,
    };
}
